import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import toast from "components/ui/toast/Toast";
import {
  useExportContactSuppressions,
  useRevealedContactSuppressions,
} from "lib/hooks/api/contacts/useContactSuppressions";
import { useFileExport } from "lib/hooks/api/exports/useFileExport";
import { Format } from "lib/types/graphQLEnums";
import { pluralise } from "lib/utils";

const today = () => new Date().toJSON().slice(0, 10);

/**
 * The contacts this company has revealed whose subject has since asked a data
 * provider to stop contacting them, as one download.
 *
 * One file rather than one per provider: a customer's job is to apply
 * do-not-contact, and splitting it made them reconcile two files to do one job.
 * The opt-out source column carries the distinction instead.
 */
export function SuppressionList() {
  const report = useRevealedContactSuppressions();
  const revealed = report.data?.revealedContactSuppressions;

  const exportSuppressions = useExportContactSuppressions();
  const file = useFileExport({
    mutation: exportSuppressions,
    getFileName: () => `stotles-suppression-list-${today()}.csv`,
    onError: () => toast.error("Couldn't prepare the download. Please try again."),
  });

  const suppressedCount = revealed?.suppressed.length ?? 0;

  return (
    <Flex column gap={24}>
      <Flex column gap={4}>
        <Text h2>Suppression list</Text>
        <Description>
          Contacts your company has revealed who have since asked our data providers to stop
          contacting them. Apply these as do-not-contact in your CRM and any outreach tool, so
          contacts you exported earlier stay compliant. Refreshed daily.
        </Description>
      </Flex>

      <Card aria-label="Suppression list">
        <Description>
          The download covers every contact your company has revealed, exported or sent to a CRM,
          from all of our contact data providers, and lists those who have since opted out along
          with which provider recorded the request.
        </Description>

        {report.isLoading && <SkeletonText width="320px" />}

        {report.isError && (
          <Description>
            We couldn&apos;t check your own contacts just now, so the download would be incomplete.
            Refresh the page, and get in touch if it keeps happening.
          </Description>
        )}

        {revealed && (
          <>
            {/* Only ever states what was found. There is no clean-bill message:
                a contact is checked against its own provider's list by that
                provider's identifier, but against the other provider's list only
                by email address, and about half of the contacts have none. */}
            {suppressedCount > 0 && (
              <Text data-testid="suppressed-summary">{suppressionSummary(suppressedCount)}</Text>
            )}

            <Flex>
              <Button
                onClick={() =>
                  exportSuppressions.mutate({ exportFormat: { fileFormat: Format.Csv } })
                }
                loading={file.isExporting}
              >
                Download CSV
              </Button>
            </Flex>
          </>
        )}
      </Card>
    </Flex>
  );
}

/** Only called with a positive count: nothing is claimed about a clean result. */
function suppressionSummary(suppressedCount: number): string {
  const verb = suppressedCount === 1 ? "has" : "have";
  return `${pluralise(suppressedCount, "revealed contact", true)} ${verb} opted out.`;
}

const Description = styled(Text)(({ theme }) => ({
  color: theme.colors.sysTextSecondary,
  maxWidth: 650,
}));

const Card = styled(Flex)(({ theme }) => ({
  flexDirection: "column",
  gap: 16,
  padding: 24,
  maxWidth: 780,
  borderRadius: 8,
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  backgroundColor: theme.colors.sysBackgroundDefault,
}));
